import { v4 as uuidv4 } from "uuid";
import { compareTwoPanelNumbers } from "../../hvSubstations/PanelNumberOrdering";
import type { Panel } from "../../shared/interfaces/Switchgear";
import type { Measure } from "../../shared/interfaces/Workflow";
import type { AssetMeasures } from "../../shared/interfaces/WorkflowV1";
import type { HvSurveyableAsset } from "../../shared/interfaces/WorkflowV2";
import type { ExtendedMeasure, GroupedMeasurement, GroupedPanel, OrderedMeasurement, WorkflowType } from "./types";

export function assetToGroupedMeasures(assetMeasures: AssetMeasures[] | undefined): GroupedMeasurement[] {
  const flattenedAssetMeasures = assetMeasures?.flatMap((asset) => {
    return asset.measures.map((measure) => {
      return {
        ...measure,
        name: asset.name,
        panelNo: asset.panelno,
        panelAssetId: asset.assetid,
        parentAssetId: asset.assetid,
      } as GroupedMeasurement & Partial<Panel>;
    });
  });

  return (
    flattenedAssetMeasures?.reduce(
      (accumulator: GroupedMeasurement[], flatMeasurement: GroupedMeasurement & Partial<Panel>) => {
        const { type, accessory, access_location, component, subloc, order } = flatMeasurement;

        const groupedPanel = {
          name: flatMeasurement.name,
          panelNo: flatMeasurement.panelNo,
          panelAssetId: flatMeasurement.panelAssetId,
          order: order,
        } as GroupedPanel;

        const foundValue = accumulator.find(
          (measurement) =>
            measurement.type === type &&
            measurement.accessory === accessory &&
            measurement.access_location === access_location &&
            measurement.component === component &&
            measurement.subloc === subloc,
        );

        if (!foundValue) {
          return (accumulator = [
            ...accumulator,
            {
              groupId: `group-${type}-${accessory}-${access_location}-${component}-${subloc}-${uuidv4()}`,
              type,
              accessory,
              access_location,
              component,
              subloc,
              panels: groupedPanel.panelNo ? [groupedPanel] : [],
            } as GroupedMeasurement,
          ]);
        } else {
          foundValue.panels = orderGroupedPanels([...foundValue.panels, groupedPanel]);
        }

        return accumulator;
      },
      [],
    ) || []
  );
}

export function groupedToAssetMeasures(groupedMeasurements: GroupedMeasurement[]): AssetMeasures[] {
  const assetMeasures = groupedMeasurements.reduce((accumulator: AssetMeasures[], groupedMeasurement) => {
    if (groupedMeasurement.panels?.length > 0) {
      groupedMeasurement.panels.forEach((panel) => {
        accumulator = addAssetMeasureToAccumulator(accumulator, panel, groupedMeasurement);
      });
    } else {
      accumulator = addAssetMeasureToAccumulator(accumulator, undefined, groupedMeasurement);
    }

    return accumulator;
  }, []);

  return assetMeasures.sort((left, right) => compareTwoPanelNumbers(left.panelno, right.panelno, "asc"));
}

export function assetToOrderedMeasurements(assetMeasures: AssetMeasures[] | undefined): OrderedMeasurement[] {
  const flattenedAssetMeasures = assetMeasures
    ?.flatMap((asset) => {
      return asset.measures.map((measure) => {
        return {
          ...measure,
          name: asset.name ?? measure.name,
          panelName: asset.name ?? measure.panelName ?? measure.name,
          panelNo: asset.panelno ?? measure.panelNo,
          panelAssetId: asset.assetid,
        } as ExtendedMeasure & Partial<Panel>;
      });
    })
    .sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER));

  return (
    flattenedAssetMeasures?.reduce(
      (accumulator: OrderedMeasurement[], flatMeasurement: ExtendedMeasure & Partial<Panel>) => {
        const {
          panelAssetId,
          type,
          access_location,
          panelNo,
          panelName,
          name,
          component,
          subloc,
          accessory,
          order,
          parentAssetId,
          parentAssetType,
        } = flatMeasurement;

        accumulator = [
          ...accumulator,
          {
            panelAssetId,
            type,
            access_location,
            panelNo,
            panelName,
            name,
            component,
            subloc,
            accessory,
            order,
            parentAssetId,
            parentAssetType,
          } as OrderedMeasurement,
        ];

        if (accumulator.some((measurement) => measurement.order === null)) {
          let index = 1;

          accumulator.forEach((measurement) => {
            measurement.order = index;
            index++;
          });
        }

        return accumulator;
      },
      [],
    ) || []
  );
}

export function groupedMeasurementsToHvSurveyableAssets(
  measurements: GroupedMeasurement[],
  workflowType: WorkflowType | undefined,
): HvSurveyableAsset[] {
  if (!workflowType) {
    return [];
  }

  const groupFunction = workflowType === "substation" ? groupByParentAssetId : groupByParentAssetType;
  const groupData = groupFunction(measurements);
  const isSubstationWorkflow = workflowType === "substation";

  return Object.entries(groupData).map(
    ([key, value]) =>
      ({
        name: isSubstationWorkflow ? [...new Set(value.map((measurement) => measurement.parentAssetName))][0] : "",
        asset_type: isSubstationWorkflow ? "" : key,
        assetid: isSubstationWorkflow ? parseInt(key, 10) : undefined,
        sub_assets: groupedToAssetMeasures(value),
      }) as HvSurveyableAsset,
  );
}

export function hvSurveyableAssetToGroupedMeasurement(hvSurveyableAssets: HvSurveyableAsset[]): GroupedMeasurement[] {
  if (!hvSurveyableAssets) {
    return [];
  }

  return hvSurveyableAssets
    .map((asset) =>
      assetToGroupedMeasures(asset.sub_assets).map((groupedMeasure) => ({
        ...groupedMeasure,
        parentAssetType: asset.asset_type,
        parentAssetId: asset.assetid,
        parentAssetName: asset.name,
      })),
    )
    .flat();
}

export function hvSurveyableAssetToOrderedMeasurement(hvSurveyableAssets: HvSurveyableAsset[]): OrderedMeasurement[] {
  if (!hvSurveyableAssets) {
    return [];
  }

  return hvSurveyableAssets
    .map((asset) =>
      assetToOrderedMeasurements(asset.sub_assets).map((orderedMeasurement) => ({
        ...orderedMeasurement,
        parentAssetType: asset.asset_type,
        parentAssetId: asset.assetid,
        parentAssetName: asset.name,
      })),
    )
    .flat();
}

function addAssetMeasureToAccumulator(
  accumulator: AssetMeasures[],
  panel: GroupedPanel | undefined,
  groupedMeasurement: GroupedMeasurement,
): AssetMeasures[] {
  const foundValue = accumulator.find(
    (assetMeasure) => (panel && assetMeasure.panelno === panel.panelNo) || assetMeasure.panelno == null,
  );

  if (!foundValue) {
    return [
      ...accumulator,
      {
        name: panel?.name || null,
        panelno: panel?.panelNo || null,
        assetid: panel?.panelAssetId || null,
        measures: [
          groupedMeasurementToMeasure(
            groupedMeasurement,
            panel?.order ?? groupedMeasurement.order,
            panel?.name,
            panel?.panelNo,
          ),
        ],
      } as AssetMeasures,
    ];
  }

  foundValue.measures = [
    ...foundValue.measures,
    groupedMeasurementToMeasure(
      groupedMeasurement,
      panel?.order ?? groupedMeasurement.order,
      foundValue.name,
      foundValue.panelno,
    ),
  ].sort((left, right) => (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER));

  return accumulator;
}

function groupedMeasurementToMeasure(
  groupedMeasurement: GroupedMeasurement,
  order: number | undefined,
  panelName?: string,
  panelNo?: string,
): Measure {
  return {
    access_location: groupedMeasurement.access_location || undefined,
    component: groupedMeasurement.component || undefined,
    type: groupedMeasurement.type || undefined,
    subloc: groupedMeasurement.subloc || undefined,
    order,
    accessory: groupedMeasurement.accessory || undefined,
    parentAssetId: groupedMeasurement.parentAssetId || undefined,
    parentAssetType: groupedMeasurement.parentAssetType || undefined,
    name: panelName != null ? panelName : undefined,
    panelName: panelName != null ? panelName : undefined,
    panelNo: panelNo != null ? panelNo : undefined,
  } as Measure;
}

function groupByParentAssetId(measurements: GroupedMeasurement[]): Record<number, GroupedMeasurement[]> {
  return measurements.reduce(
    (accumulator, measurement) => {
      if (!measurement.parentAssetId) {
        return accumulator;
      }

      if (!accumulator[measurement.parentAssetId]) {
        accumulator[measurement.parentAssetId] = [];
      }

      accumulator[measurement.parentAssetId].push(measurement);

      return accumulator;
    },
    {} as Record<number, GroupedMeasurement[]>,
  );
}

function groupByParentAssetType(measurements: GroupedMeasurement[]): Record<string, GroupedMeasurement[]> {
  return measurements.reduce(
    (accumulator, measurement) => {
      if (!measurement.parentAssetType) {
        return accumulator;
      }

      if (!accumulator[measurement.parentAssetType]) {
        accumulator[measurement.parentAssetType] = [];
      }

      accumulator[measurement.parentAssetType].push(measurement);

      return accumulator;
    },
    {} as Record<string, GroupedMeasurement[]>,
  );
}

function orderGroupedPanels(groupedPanels: GroupedPanel[]): GroupedPanel[] {
  return [...groupedPanels].sort((left, right) => compareTwoPanelNumbers(left.panelNo, right.panelNo, "asc"));
}
